import { JSX, Match, Switch, createEffect, createSignal, on } from 'solid-js';
import { Spinner } from '@tcweb/connect-ui';

interface ThumbnailLoaderProps {
  thumbnailUrl: string;
  altText: string;
  /**
   * The placeholder image to show when the thumbnail image fails to load.
   * If not provided, the default placeholder image will be used.
   *
   * The value must be a valid URL not requiring authentication or a BASE64 encoded image.
   */
  placeholderUrl?: string;
  /**
   * The class to apply to the container element.
   */
  class?: string;
  /**
   * The class to apply to the image element.
   */
  imageClass?: string;

  /**
   * Optional function to fetch the thumbnail blob.
   * If not provided, the component will fetch the image using native fetch.
   *
   * Should return a Promise resolving to a URL.createObjectURL of the image blob:
   * ```
   * // Example:
   * return URL.createObjectURL(response.data!);
   * ```
   */
  getThumbnailBlob?(thumbnailUrl: string): Promise<string>;
}

export const ThumbnailLoader = (props: ThumbnailLoaderProps): JSX.Element => {
  const [isLoading, setIsLoading] = createSignal(true);
  const [imageLoadFailed, setImageLoadFailed] = createSignal(false);
  const [thumbnail, setThumbnail] = createSignal<string | undefined>(undefined);

  // Default fetch function using native fetch
  const defaultFetchBlob = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  createEffect(
    on(
      [() => props.thumbnailUrl, () => props.getThumbnailBlob],
      ([thumbnailUrl, getThumbnailBlob]) => {
        setIsLoading(true);
        setImageLoadFailed(false);
        setThumbnail(undefined);

        // Use provided getThumbnailBlob or default to native fetch
        const fetchBlob = getThumbnailBlob
          ? getThumbnailBlob(thumbnailUrl)
          : defaultFetchBlob(thumbnailUrl);

        fetchBlob
          .then((data) => {
            setThumbnail(data);
          })
          .catch(() => {
            setImageLoadFailed(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    )
  );

  return (
    <div
      data-testid="thumbnail-loader"
      class={`flex justify-center items-center h-full w-full relative overflow-hidden ${props.class ?? ''}`}
    >
      <Switch>
        <Match when={isLoading()}>
          <div
            data-testid="spinner"
            class="flex flex-1 items-center justify-center"
          >
            <Spinner show={true} />
          </div>
        </Match>

        <Match when={imageLoadFailed() && !props.placeholderUrl}>
          <div
            data-testid="placeholder-icon"
            class="flex flex-1 items-center justify-center"
          >
            <modus-icon name="image" size="48" class="text-gray-400" />
          </div>
        </Match>

        <Match when={imageLoadFailed() && props.placeholderUrl} keyed>
          {(placeholderUrl) => (
            <img
              data-testid="placeholder-image"
              src={placeholderUrl}
              alt={props.altText}
              class={`w-full h-full object-cover ${props.imageClass ?? ''}`}
            />
          )}
        </Match>

        <Match when={!isLoading() && !imageLoadFailed() && thumbnail()} keyed>
          {(thumbnail) => (
            <img
              data-testid="loaded-image"
              src={thumbnail}
              alt={props.altText}
              class={`object-cover ${props.imageClass ?? ''}`}
              onError={() => {
                // Should show placeholder icon when image fetching fails
                setImageLoadFailed(true);
              }}
            />
          )}
        </Match>
      </Switch>
    </div>
  );
};
